import React, { useEffect, useState } from "react";
import { Plus, Trash2, Star, StarOff, Loader2, X, Video } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";

/**
 * Admin → Kids Channel tab.
 * - List every video that lives on the Kids channel (category=kids OR is_kid=true)
 * - Post directly into the Kids channel (bypasses parent-approval, admin owns QA)
 * - Toggle Family Channel feature (feature_on_family flag)
 * - Remove Kids videos
 */
export default function KidsAdminTab() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composer, setComposer] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", video_url: "", thumbnail_url: "", duration_sec: 0, feature_on_family: true });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/kids/videos");
      setVideos(data.videos || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const post = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.video_url.trim()) return toast.error("Title + Video URL required");
    setBusy(true);
    try {
      await api.post("/admin/kids/videos/post", { ...form, title: form.title.trim(), description: form.description.trim(), video_url: form.video_url.trim(), thumbnail_url: form.thumbnail_url.trim() });
      toast.success("Posted to Kids channel");
      setForm({ title: "", description: "", video_url: "", thumbnail_url: "", duration_sec: 0, feature_on_family: true });
      setComposer(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const toggleFeature = async (v) => {
    try {
      const { data } = await api.post(`/admin/kids/videos/${v.id}/feature`, { feature_on_family: !v.feature_on_family });
      setVideos((vs) => vs.map((x) => (x.id === v.id ? { ...x, feature_on_family: data.feature_on_family } : x)));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (v) => {
    if (!window.confirm(`Remove "${v.title}" from the Kids channel?`)) return;
    try {
      await api.delete(`/admin/kids/videos/${v.id}`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <div className="text-white/60">Loading Kids videos…</div>;

  const pending = videos.filter((v) => !v.admin_approved_kid || v.parent_approved === false);
  const live = videos.filter((v) => v.admin_approved_kid);

  return (
    <div data-testid="kids-admin-tab" className="space-y-8">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-tv-accent font-bold flex items-center gap-2">
            <img src="/tapintv-kids-logo.png" alt="Kids" className="h-6" /> Kids Channel
          </div>
          <h2 className="font-display font-black text-4xl text-white tracking-tighter mt-1">Manage Kids Content</h2>
          <p className="text-white/60 text-sm mt-2">Post directly, toggle Family Channel visibility, or remove kid-unsafe items. Your posts skip the parent-approval queue.</p>
        </div>
        <button
          data-testid="kids-admin-new-btn"
          onClick={() => setComposer(!composer)}
          className="bg-tv-accent hover:bg-yellow-400 text-black font-bold px-4 py-2 rounded-full inline-flex items-center gap-2"
        >
          {composer ? <><X className="w-4 h-4" /> Close</> : <><Plus className="w-4 h-4" /> Post Kids video</>}
        </button>
      </div>

      {/* Stats row */}
      <section className="grid sm:grid-cols-3 gap-3">
        <Tile label="Live on Kids" value={live.length} color="emerald" />
        <Tile label="Pending (parent/admin)" value={pending.length} color="amber" />
        <Tile label="Featured on Family" value={videos.filter((v) => v.feature_on_family).length} color="accent" />
      </section>

      {/* Composer */}
      {composer && (
        <form onSubmit={post} data-testid="kids-admin-composer" className="tv-glass rounded-2xl border border-white/10 p-6 space-y-3">
          <h3 className="font-display font-bold text-xl text-white">Post Kids video</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Title *" testid="kids-admin-title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
            <Field label="Duration (seconds)" testid="kids-admin-duration" type="number" value={form.duration_sec} onChange={(v) => setForm({ ...form, duration_sec: Number(v) || 0 })} />
            <Field label="Video URL *" testid="kids-admin-video-url" value={form.video_url} onChange={(v) => setForm({ ...form, video_url: v })} placeholder="/files/kids/episode-12.mp4 or https://youtube.com/embed/..." wide />
            <Field label="Thumbnail URL" testid="kids-admin-thumb" value={form.thumbnail_url} onChange={(v) => setForm({ ...form, thumbnail_url: v })} wide />
          </div>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-white/60 font-bold">Description</span>
            <textarea
              data-testid="kids-admin-desc"
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-tv-accent"
            />
          </label>
          <label className="flex items-center gap-2 text-white/85 text-sm">
            <input
              type="checkbox"
              data-testid="kids-admin-feature"
              checked={form.feature_on_family}
              onChange={(e) => setForm({ ...form, feature_on_family: e.target.checked })}
              className="accent-tv-accent"
            />
            Also feature on the Family Channel
          </label>
          <button
            data-testid="kids-admin-submit"
            disabled={busy}
            className="bg-tv-accent hover:bg-yellow-400 text-black font-bold px-5 py-2 rounded-full text-sm inline-flex items-center gap-2 disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {busy ? "Posting…" : "Post to Kids"}
          </button>
        </form>
      )}

      {/* List */}
      <section className="tv-glass rounded-2xl border border-white/10 p-4 sm:p-6">
        <h3 className="font-display font-bold text-xl text-white mb-3">All Kids videos ({videos.length})</h3>
        {videos.length === 0 ? (
          <div className="text-white/50 text-sm">Nothing posted yet. Use the composer above to add the first video.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {videos.map((v) => (
              <div
                key={v.id}
                data-testid={`kids-admin-row-${v.id}`}
                className="rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden flex flex-col"
              >
                <div className="aspect-video bg-black relative">
                  {v.thumbnail_url ? (
                    <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white/30"><Video className="w-8 h-8" /></div>
                  )}
                  {!v.admin_approved_kid && (
                    <span className="absolute top-2 left-2 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-amber-500/80 text-black">Pending</span>
                  )}
                  {v.feature_on_family && (
                    <span className="absolute top-2 right-2 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-tv-accent text-black">Family</span>
                  )}
                </div>
                <div className="p-3 flex-1 flex flex-col">
                  <div className="font-display font-bold text-white text-sm line-clamp-2">{v.title}</div>
                  <div className="text-[11px] text-white/50 mt-1">{v.creator_name} · {new Date(v.created_at).toLocaleDateString()}</div>
                  <div className="mt-auto pt-3 flex gap-2">
                    <button
                      data-testid={`kids-admin-feature-${v.id}`}
                      onClick={() => toggleFeature(v)}
                      title={v.feature_on_family ? "Remove from Family rail" : "Add to Family rail"}
                      className="text-xs bg-white/5 hover:bg-white/10 text-white/80 px-2 py-1.5 rounded-full inline-flex items-center gap-1"
                    >
                      {v.feature_on_family ? <><StarOff className="w-3 h-3" /> Unfeature</> : <><Star className="w-3 h-3" /> Feature</>}
                    </button>
                    <button
                      data-testid={`kids-admin-remove-${v.id}`}
                      onClick={() => remove(v)}
                      className="ml-auto text-xs bg-red-500/10 hover:bg-red-500/20 text-red-300 px-2 py-1.5 rounded-full inline-flex items-center gap-1"
                    >
                      <Trash2 className="w-3 h-3" /> Remove
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function Tile({ label, value, color = "accent" }) {
  const styles = {
    emerald: "border-emerald-500/30 bg-emerald-500/5 text-emerald-300",
    amber: "border-amber-500/30 bg-amber-500/5 text-amber-300",
    accent: "border-tv-accent/30 bg-tv-accent/5 text-tv-accent",
  }[color];
  return (
    <div className={`rounded-xl border p-4 ${styles}`}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-white/50">{label}</div>
      <div className="font-display font-black text-3xl mt-1">{value}</div>
    </div>
  );
}

function Field({ label, value, onChange, testid, placeholder, type = "text", wide }) {
  return (
    <label className={`block ${wide ? "sm:col-span-2" : ""}`}>
      <span className="text-[10px] uppercase tracking-wider text-white/60 font-bold">{label}</span>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-tv-accent"
      />
    </label>
  );
}
